import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { LinkType } from '../types';

interface QuickAddDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAddLink: (link: LinkType) => void;
}

const QuickAddDialog: React.FC<QuickAddDialogProps> = ({ isOpen, onClose, onAddLink }) => {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');

  useEffect(() => {
    if (isOpen) {
      setUrl('');
      setTitle('');
      setDescription('');
      setTags('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Auto fetch favicon or logo upon entering url
  const handleUrlChange = (val: string) => {
    setUrl(val);

    // Simple heuristic to extract a possible title from the URL if title is empty
    if (!title && val) {
      try {
        const u = new URL(val.startsWith('http') ? val : `https://${val}`);
        const domainParts = u.hostname.replace('www.', '').split('.');
        const inferredTitle = domainParts[0].charAt(0).toUpperCase() + domainParts[0].slice(1);
        setTitle(inferredTitle);
      } catch {
        // invalid URL ignore
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;

    let finalUrl = url.trim();
    if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
      finalUrl = `https://${finalUrl}`;
    }

    let favicon = '';
    try {
      const u = new URL(finalUrl);
      favicon = `${u.protocol}//${u.hostname}/favicon.ico`;
    } catch {
      // ignore
    }

    const parsedTags = tags
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t !== '');

    const newLink: LinkType = {
      id: uuidv4(),
      title: title.trim() || finalUrl,
      url: finalUrl,
      description: description.trim(),
      favicon,
      tags: parsedTags,
      status: 'approved',
      position: { x: 100 + Math.random() * 300, y: 100 + Math.random() * 300 },
      folderIds: [],
      createdBy: '',
      createdAt: Date.now(),
    };

    onAddLink(newLink);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 font-heebo" dir="rtl">
      <div className="w-full max-w-md bg-white border border-solid border-black p-6 rounded-none shadow-none">
        <div className="flex items-center justify-between border-b border-solid border-black pb-3 mb-4">
          <h2 className="text-md font-black text-black">⚡ הוספת קישור מהירה (Ctrl+K)</h2>
          <button
            onClick={onClose}
            className="text-xs p-1 border border-solid border-black hover:bg-gray-100 rounded-none cursor-pointer font-bold"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">
              כתובת קישור (URL): *
            </label>
            <input
              type="text"
              required
              className="w-full px-2 py-1.5 border border-solid border-black text-xs outline-none bg-white rounded-none shadow-none font-sans"
              placeholder="example.com"
              value={url}
              onChange={(e) => handleUrlChange(e.target.value)}
              dir="ltr"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">
              כותרת הקישור:
            </label>
            <input
              type="text"
              className="w-full px-2 py-1.5 border border-solid border-black text-xs outline-none bg-white rounded-none shadow-none"
              placeholder="שם האתר / התיעוד"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              dir="rtl"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">
              תיאור קצר:
            </label>
            <input
              type="text"
              className="w-full px-2 py-1.5 border border-solid border-black text-xs outline-none bg-white rounded-none shadow-none"
              placeholder="מה יש בקישור? תיאור קצר..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              dir="rtl"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">
              תגיות (מופרדות בפסיק):
            </label>
            <input
              type="text"
              className="w-full px-2 py-1.5 border border-solid border-black text-xs outline-none bg-white rounded-none shadow-none"
              placeholder="עבודה, עיצוב, השראה"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              dir="rtl"
            />
          </div>

          <div className="flex gap-2 pt-2 justify-end">
            <button
              type="submit"
              className="px-4 py-2 bg-black hover:bg-gray-900 text-white font-bold text-xs border border-solid border-black hover:border-gray-900 transition-colors rounded-none shadow-none cursor-pointer"
            >
              הוסף לקנבס
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white hover:bg-gray-50 text-black font-bold text-xs border border-solid border-black transition-colors rounded-none shadow-none cursor-pointer"
            >
              ביטול
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickAddDialog;